import React from "react";
import { Field, TextInput, Textarea, Select, Honeypot, SubmitRow, Confirmation } from "./Form";
import { useFormState, useSubmit, firstErrorFocus } from "../hooks/useForm";
import { requiredError, emailError } from "../lib/workEmail";

const SECTORS = [
  { value: "financial-services", label: "Financial services" },
  { value: "health", label: "Health" },
  { value: "public-sector", label: "Public sector" },
  { value: "telecommunications", label: "Telecommunications" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "professional-services", label: "Professional services" },
  { value: "technology", label: "Technology" },
  { value: "other", label: "Other" },
];

const HEADCOUNTS = [
  { value: "under-100", label: "Under 100" },
  { value: "100-999", label: "100–999" },
  { value: "1000-2999", label: "1,000–2,999" },
  { value: "3000-plus", label: "3,000+" },
];

const INITIAL = { organisation: "", country: "", name: "", role: "", email: "", sector: "", headcount: "", first_use: "", hp: "" };

export default function EarlyAccessForm() {
  const { values, errors, setErrors, update } = useFormState(INITIAL);
  const { pending, done, serverError, submit } = useSubmit("early-access");

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = {
      organisation: requiredError(values.organisation),
      country: requiredError(values.country),
      name: requiredError(values.name),
      role: requiredError(values.role),
      email: emailError(values.email),
      sector: requiredError(values.sector),
      headcount: requiredError(values.headcount),
    };
    setErrors(next);
    if (Object.values(next).some(Boolean)) return firstErrorFocus(next);
    await submit({
      organisation: values.organisation.trim(),
      country: values.country.trim(),
      name: values.name.trim(),
      role: values.role.trim(),
      email: values.email.trim(),
      sector: values.sector,
      headcount: values.headcount,
      first_use: values.first_use.trim() || null,
      hp: values.hp,
    });
  };

  if (done) {
    return <Confirmation testId="early-access-confirmation">Thank you. We review applications weekly and will reply to the email given.</Confirmation>;
  }

  return (
    <form onSubmit={onSubmit} className="form" noValidate data-testid="early-access-form">
      <div className="form-row">
        <Field label="Organisation" htmlFor="organisation" error={errors.organisation}>
          <TextInput id="organisation" name="organisation" value={values.organisation} onChange={update("organisation")} autoComplete="organization" invalid={!!errors.organisation} testId="ea-organisation" />
        </Field>
        <Field label="Country" htmlFor="country" error={errors.country}>
          <TextInput id="country" name="country" value={values.country} onChange={update("country")} autoComplete="country-name" invalid={!!errors.country} testId="ea-country" />
        </Field>
      </div>
      <div className="form-row">
        <Field label="Your name" htmlFor="name" error={errors.name}>
          <TextInput id="name" name="name" value={values.name} onChange={update("name")} autoComplete="name" invalid={!!errors.name} testId="ea-name" />
        </Field>
        <Field label="Role" htmlFor="role" error={errors.role}>
          <TextInput id="role" name="role" value={values.role} onChange={update("role")} autoComplete="organization-title" invalid={!!errors.role} testId="ea-role" />
        </Field>
      </div>
      <Field label="Work email" htmlFor="email" error={errors.email}>
        <TextInput id="email" name="email" type="email" value={values.email} onChange={update("email")} autoComplete="email" invalid={!!errors.email} testId="ea-email" />
      </Field>
      <div className="form-row">
        <Field label="Sector" htmlFor="sector" error={errors.sector}>
          <Select id="sector" name="sector" value={values.sector} onChange={update("sector")} options={SECTORS} invalid={!!errors.sector} testId="ea-sector" />
        </Field>
        <Field label="Headcount" htmlFor="headcount" error={errors.headcount}>
          <Select id="headcount" name="headcount" value={values.headcount} onChange={update("headcount")} options={HEADCOUNTS} invalid={!!errors.headcount} testId="ea-headcount" />
        </Field>
      </div>
      <Field label="One sentence on where you want AI working first" htmlFor="first_use" optional>
        <Textarea id="first_use" name="first_use" rows={3} value={values.first_use} onChange={update("first_use")} maxLength={500} testId="ea-first-use" />
      </Field>
      <Honeypot value={values.hp} onChange={update("hp")} />
      <SubmitRow pending={pending} label="Apply for early access" testId="ea-submit" error={serverError} />
    </form>
  );
}
